import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  TreePine, Phone, Mail, MapPin, Menu, X, 
  ChevronDown, Facebook, Instagram, Shield, Truck, Flame, 
  Scissors, Wrench, Hammer, ArrowRight 
} from 'lucide-react';
import { COMPANY_INFO } from '../data';

// Scroll Restoration Component
export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function Layout({ children }) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSkovserviceOpen, setIsSkovserviceOpen] = useState(false);
  const [isNaturplejeOpen, setIsNaturplejeOpen] = useState(false);
  const [isVognmandOpen, setIsVognmandOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Track window width to handle the strict 1200px breakpoint for mobile menu
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 1200) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Ensure scripts/state reset on route change
  useEffect(() => {
    setIsSkovserviceOpen(false);
    setIsNaturplejeOpen(false);
    setIsVognmandOpen(false);
    setIsMobileMenuOpen(false);

    const s = document.createElement("script");
    s.src = "https://cdn.iubenda.com/iubenda.js";
    s.async = true;
    document.body.appendChild(s);
  }, [location.pathname]);

  const isMobile = windowWidth < 1200;

  const skovserviceLinks = [
    { label: "Maskinskovning og udkørsel", href: "/skovservice/maskinskovning" },
    { label: "Flishugning", href: "/skovservice/flishugning" },
    { label: "Gravemaskine", href: "/skovservice/gravemaskine" }
  ];

  const naturplejeLinks = [
    { label: "Brakpudsning", href: "/naturpleje/brakpudsning" },
    { label: "Grenknusning", href: "/naturpleje/grenknusning" },
    { label: "Hegnsklipning", href: "/naturpleje/hegnsklipning" },
    { label: "Bomford Flailbot", href: "/naturpleje/bomford-flailbot" }
  ];

  const vognmandLinks = [
    { label: "Walking floor trailer", href: "/walking-floor-trailer" },
    { label: "Blokvognstransport", href: "/blokvognstransport" }
  ];

  const otherMainLinks = [
    { label: "Stub- & Rodfræsning", href: "/stub-og-rodfraesning" },
    { label: "Om os", href: "/om-os" },
    { label: "Galleri", href: "/galleri" },
    { label: "Kontakt", href: "/kontakt" }
  ];

  const isVognmandActive = location.pathname.startsWith('/vognmand') || 
                           location.pathname.startsWith('/walking-floor-trailer') || 
                           location.pathname.startsWith('/blokvognstransport');

  const isActive = (path) => {
    if (path === '/' && location.pathname !== '/') return false;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex flex-col bg-stone-950 text-stone-100 antialiased font-sans selection:bg-emerald-800 selection:text-white">
      <ScrollToTop />

      {/* Top Bar */}
      <div className="bg-stone-800 border-b border-stone-700 text-xs py-2.5 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-stone-400">
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <MapPin className="w-3.5 h-3.5 text-emerald-500" />
              {COMPANY_INFO.address}
            </span>
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <Shield className="w-3.5 h-3.5 text-emerald-500" />
              CVR: {COMPANY_INFO.cvr}
            </span>
          </div>
          <div className="flex items-center gap-6 text-stone-400">
            <a href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`} className="flex items-center gap-1.5 hover:text-emerald-400 transition-colors whitespace-nowrap">
              <Phone className="w-3.5 h-3.5 text-emerald-500" />
              {COMPANY_INFO.phone}
            </a>
            <a href={`mailto:${COMPANY_INFO.email}`} className="flex items-center gap-1.5 hover:text-emerald-400 transition-colors whitespace-nowrap">
              <Mail className="w-3.5 h-3.5 text-emerald-500" />
              {COMPANY_INFO.email}
            </a>
          </div>
        </div>
      </div>

      {/* Navigation Header */}
      <header className="sticky top-0 z-50 bg-[#3a6e44] backdrop-blur-md border-b border-[#2d5635]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/1d97ae2c-327a-4371-a485-93dbef054f5c.svg" 
              alt="Vesthimmerlands Maskinskovning ApS Logo" 
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation (Only shown when screen >= 1200px) */}
          {!isMobile && (
            <nav className="flex items-center gap-1">
              {/* Forside */}
              <Link 
                to="/" 
                className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                  location.pathname === '/' 
                    ? 'text-emerald-400 bg-emerald-950/40 border border-emerald-900/50' 
                    : 'text-stone-300 hover:text-white hover:bg-stone-900/50'
                }`}
              >
                Forside
              </Link>

              {/* Skovservice Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setIsSkovserviceOpen(true)}
                onMouseLeave={() => setIsSkovserviceOpen(false)}
              >
                <button 
                  onClick={() => {
                    setIsSkovserviceOpen(!isSkovserviceOpen);
                    setIsNaturplejeOpen(false);
                    setIsVognmandOpen(false);
                  }}
                  className={`px-3 py-2 rounded-md text-sm font-semibold flex items-center gap-1.5 transition-all ${
                    isActive('/skovservice') 
                      ? 'text-emerald-400 bg-emerald-950/40 border border-emerald-900/50' 
                      : 'text-stone-300 hover:text-white hover:bg-stone-900/50'
                  }`}
                >
                  Skovservice
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isSkovserviceOpen ? 'rotate-180' : ''}`} />
                </button>
                {isSkovserviceOpen && (
                  <div className="absolute left-0 top-full pt-1.5 w-56 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                    <div className="bg-stone-900 border border-stone-800 rounded-lg shadow-xl py-1.5">
                      <Link 
                        to="/skovservice" 
                        className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                          location.pathname === '/skovservice'
                            ? 'text-emerald-400 bg-emerald-950/20 font-semibold'
                            : 'text-stone-300 hover:text-white hover:bg-stone-800/50'
                        }`}
                        onClick={() => setIsSkovserviceOpen(false)}
                      >
                        Skovservice
                      </Link>
                      {skovserviceLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                            isActive(link.href)
                              ? 'text-emerald-400 bg-emerald-950/20 font-semibold'
                              : 'text-stone-300 hover:text-white hover:bg-stone-800/50'
                          }`}
                          onClick={() => setIsSkovserviceOpen(false)}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Naturpleje Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setIsNaturplejeOpen(true)}
                onMouseLeave={() => setIsNaturplejeOpen(false)}
              >
                <button 
                  onClick={() => {
                    setIsNaturplejeOpen(!isNaturplejeOpen);
                    setIsSkovserviceOpen(false);
                    setIsVognmandOpen(false);
                  }}
                  className={`px-3 py-2 rounded-md text-sm font-semibold flex items-center gap-1.5 transition-all ${
                    isActive('/naturpleje') 
                      ? 'text-emerald-400 bg-emerald-950/40 border border-emerald-900/50' 
                      : 'text-stone-300 hover:text-white hover:bg-stone-900/50'
                  }`}
                >
                  Naturpleje
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isNaturplejeOpen ? 'rotate-180' : ''}`} />
                </button>
                {isNaturplejeOpen && (
                  <div className="absolute left-0 top-full pt-1.5 w-56 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                    <div className="bg-stone-900 border border-stone-800 rounded-lg shadow-xl py-1.5">
                      <Link 
                        to="/naturpleje" 
                        className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                          location.pathname === '/naturpleje'
                            ? 'text-emerald-400 bg-emerald-950/20 font-semibold'
                            : 'text-stone-300 hover:text-white hover:bg-stone-800/50'
                        }`}
                        onClick={() => setIsNaturplejeOpen(false)}
                      >
                        Naturpleje
                      </Link>
                      {naturplejeLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                            isActive(link.href)
                              ? 'text-emerald-400 bg-emerald-950/20 font-semibold'
                              : 'text-stone-300 hover:text-white hover:bg-stone-800/50'
                          }`}
                          onClick={() => setIsNaturplejeOpen(false)}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Vognmandskørsel Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setIsVognmandOpen(true)}
                onMouseLeave={() => setIsVognmandOpen(false)}
              >
                <button 
                  onClick={() => {
                    setIsVognmandOpen(!isVognmandOpen);
                    setIsSkovserviceOpen(false);
                    setIsNaturplejeOpen(false);
                  }}
                  className={`px-3 py-2 rounded-md text-sm font-semibold flex items-center gap-1.5 transition-all ${
                    isVognmandActive 
                      ? 'text-emerald-400 bg-emerald-950/40 border border-emerald-900/50' 
                      : 'text-stone-300 hover:text-white hover:bg-stone-900/50'
                  }`}
                >
                  Vognmandskørsel
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isVognmandOpen ? 'rotate-180' : ''}`} />
                </button>
                {isVognmandOpen && (
                  <div className="absolute left-0 top-full pt-1.5 w-60 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                    <div className="bg-stone-900 border border-stone-800 rounded-lg shadow-xl py-1.5">
                      <Link 
                        to="/vognmand" 
                        className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                          location.pathname === '/vognmand'
                            ? 'text-emerald-400 bg-emerald-950/20 font-semibold'
                            : 'text-stone-300 hover:text-white hover:bg-stone-800/50'
                        }`}
                        onClick={() => setIsVognmandOpen(false)}
                      >
                        Vognmandskørsel
                      </Link>
                      {vognmandLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                            location.pathname === link.href
                              ? 'text-emerald-400 bg-emerald-950/20 font-semibold'
                              : 'text-stone-300 hover:text-white hover:bg-stone-800/50'
                          }`}
                          onClick={() => setIsVognmandOpen(false)}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Other Links */}
              {otherMainLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-3 py-2 rounded-md text-sm font-semibold transition-all ${
                    isActive(link.href)
                      ? 'text-emerald-400 bg-emerald-950/40 border border-emerald-900/50'
                      : 'text-stone-300 hover:text-white hover:bg-stone-900/50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              {/* CTA Button */}
              <Link 
                to="/kontakt" 
                className="ml-4 bg-swamp-900 hover:bg-swamp-800 text-white font-bold px-5 py-2.5 rounded-lg text-sm tracking-wide transition-all shadow-lg shadow-black/40 border border-swamp-700 hover:border-swamp-500 hover:scale-[1.02] flex items-center gap-1.5 cursor-pointer"
              >
                Få et tilbud
                <ArrowRight className="w-4 h-4 text-swamp-300 stroke-[2.5]" />
              </Link>
            </nav>
          )}

          {/* Mobile Menu Button (Only shown when screen < 1200px) */}
          {isMobile && (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2.5 rounded-lg bg-stone-900 border border-stone-800 text-stone-300 hover:text-white hover:bg-stone-800 transition-all"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          )}
        </div>

        {/* Mobile Navigation Panel */}
        {isMobile && isMobileMenuOpen && (
          <div className="border-t border-stone-900 bg-stone-950 max-h-[calc(100vh-80px)] overflow-y-auto animate-in slide-in-from-top duration-200">
            <div className="px-4 py-6 space-y-4">
              {/* Main Links */}
              <div className="space-y-1">
                <Link
                  to="/"
                  className={`block px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                    location.pathname === '/'
                      ? 'text-emerald-400 bg-emerald-950/30 border border-emerald-900/40'
                      : 'text-stone-300 hover:text-white hover:bg-stone-900'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Forside
                </Link>

                {/* Skovservice Accordion */}
                <div className="space-y-1">
                  <button
                    onClick={() => setIsSkovserviceOpen(!isSkovserviceOpen)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                      isActive('/skovservice')
                        ? 'text-emerald-400 bg-emerald-950/20'
                        : 'text-stone-300 hover:text-white hover:bg-stone-900'
                    }`}
                  >
                    <span>Skovservice</span>
                    <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isSkovserviceOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isSkovserviceOpen && (
                    <div className="pl-4 pr-2 py-1 space-y-1 bg-stone-900/40 rounded-lg border border-stone-900/60">
                      <Link
                        to="/skovservice"
                        className={`block px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                          location.pathname === '/skovservice'
                            ? 'text-emerald-400 font-semibold'
                            : 'text-stone-400 hover:text-white'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Skovservice
                      </Link>
                      {skovserviceLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          className={`block px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                            isActive(link.href)
                              ? 'text-emerald-400 font-semibold bg-emerald-950/10'
                              : 'text-stone-400 hover:text-white'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Naturpleje Accordion */}
                <div className="space-y-1">
                  <button
                    onClick={() => setIsNaturplejeOpen(!isNaturplejeOpen)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                      isActive('/naturpleje')
                        ? 'text-emerald-400 bg-emerald-950/20'
                        : 'text-stone-300 hover:text-white hover:bg-stone-900'
                    }`}
                  >
                    <span>Naturpleje</span>
                    <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isNaturplejeOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isNaturplejeOpen && (
                    <div className="pl-4 pr-2 py-1 space-y-1 bg-stone-900/40 rounded-lg border border-stone-900/60">
                      <Link
                        to="/naturpleje"
                        className={`block px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                          location.pathname === '/naturpleje'
                            ? 'text-emerald-400 font-semibold'
                            : 'text-stone-400 hover:text-white'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Naturpleje
                      </Link>
                      {naturplejeLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          className={`block px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                            isActive(link.href)
                              ? 'text-emerald-400 font-semibold bg-emerald-950/10'
                              : 'text-stone-400 hover:text-white'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Vognmandskørsel Accordion */}
                <div className="space-y-1">
                  <button
                    onClick={() => setIsVognmandOpen(!isVognmandOpen)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                      isVognmandActive
                        ? 'text-emerald-400 bg-emerald-950/20'
                        : 'text-stone-300 hover:text-white hover:bg-stone-900'
                    }`}
                  >
                    <span>Vognmandskørsel</span>
                    <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isVognmandOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isVognmandOpen && (
                    <div className="pl-4 pr-2 py-1 space-y-1 bg-stone-900/40 rounded-lg border border-stone-900/60">
                      <Link
                        to="/vognmand"
                        className={`block px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                          location.pathname === '/vognmand'
                            ? 'text-emerald-400 font-semibold'
                            : 'text-stone-400 hover:text-white'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Vognmandskørsel
                      </Link>
                      {vognmandLinks.map((link) => (
                        <Link
                          key={link.href}
                          to={link.href}
                          className={`block px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                            location.pathname === link.href
                              ? 'text-emerald-400 font-semibold bg-emerald-950/10'
                              : 'text-stone-400 hover:text-white'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Other Main Links */}
                <Link
                  to="/stub-og-rodfraesning"
                  className={`block px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                    isActive('/stub-og-rodfraesning')
                      ? 'text-emerald-400 bg-emerald-950/30 border border-emerald-900/40'
                      : 'text-stone-300 hover:text-white hover:bg-stone-900'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Stub- & Rodfræsning
                </Link>

                <Link
                  to="/om-os"
                  className={`block px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                    isActive('/om-os')
                      ? 'text-emerald-400 bg-emerald-950/30 border border-emerald-900/40'
                      : 'text-stone-300 hover:text-white hover:bg-stone-900'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Om os
                </Link>

                <Link
                  to="/galleri"
                  className={`block px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                    isActive('/galleri')
                      ? 'text-emerald-400 bg-emerald-950/30 border border-emerald-900/40'
                      : 'text-stone-300 hover:text-white hover:bg-stone-900'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Galleri
                </Link>

                <Link
                  to="/kontakt"
                  className={`block px-4 py-3 rounded-lg text-base font-semibold transition-all ${
                    isActive('/kontakt')
                      ? 'text-emerald-400 bg-emerald-950/30 border border-emerald-900/40'
                      : 'text-stone-300 hover:text-white hover:bg-stone-900'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Kontakt
                </Link>
              </div>

              {/* Mobile CTA */}
              <div className="pt-4 border-t border-stone-900">
                <Link
                  to="/kontakt"
                  className="w-full bg-swamp-900 hover:bg-swamp-800 text-white font-bold py-3.5 rounded-xl text-center tracking-wide transition-all shadow-lg block uppercase text-sm border border-swamp-700"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Få et uforpligtende tilbud
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content Area */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#3a6e44] border-t border-[#2d5635] text-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Column 1: Brand & Bio */}
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <img 
                  src="https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/1d97ae2c-327a-4371-a485-93dbef054f5c.svg" 
                  alt="Vesthimmerlands Maskinskovning ApS Logo" 
                  className="h-10 w-auto object-contain"
                />
              </div>
              <p className="text-sm text-stone-400 leading-relaxed">
                Professionel maskinskovning, skovservice og naturpleje i hele Danmark. Vi er godkendt til arbejde i PEFC-certificerede skove og leverer altid høj driftssikkerhed og kvalitet.
              </p>
              <div className="flex items-center gap-3">
                <a 
                  href={COMPANY_INFO.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-lg bg-stone-950 border border-stone-800 text-stone-400 hover:text-white hover:bg-stone-800 transition-colors"
                  aria-label="Følg os på Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a 
                  href="https://www.instagram.com/vesthimmerlandsmaskinskovning/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-lg bg-stone-950 border border-stone-800 text-stone-400 hover:text-white hover:bg-stone-800 transition-colors"
                  aria-label="Følg os på Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Column 2: Quick Links */}
            <div>
              <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Ydelser</h3>
              <ul className="space-y-3.5 text-sm">
                <li>
                  <Link to="/vognmand" className="text-stone-400 hover:text-emerald-400 transition-colors">
                    Vognmandskørsel
                  </Link>
                </li>
                <li>
                  <Link to="/walking-floor-trailer" className="text-stone-400 hover:text-emerald-400 transition-colors">
                    Walking floor trailer
                  </Link>
                </li>
                <li>
                  <Link to="/blokvognstransport" className="text-stone-400 hover:text-emerald-400 transition-colors">
                    Blokvognstransport
                  </Link>
                </li>
                <li>
                  <Link to="/skovservice/maskinskovning" className="text-stone-400 hover:text-emerald-400 transition-colors">
                    Maskinskovning
                  </Link>
                </li>
                <li>
                  <Link to="/skovservice/flishugning" className="text-stone-400 hover:text-emerald-400 transition-colors">
                    Flishugning
                  </Link>
                </li>
                <li>
                  <Link to="/naturpleje" className="text-stone-400 hover:text-emerald-400 transition-colors">
                    Naturpleje &amp; grenknusning
                  </Link>
                </li>
                <li>
                  <Link to="/stub-og-rodfraesning" className="text-stone-400 hover:text-emerald-400 transition-colors">
                    Stub- &amp; Rodfræsning
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 3: Info */}
            <div>
              <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Virksomheden</h3>
              <ul className="space-y-3.5 text-sm">
                <li>
                  <Link to="/om-os" className="text-stone-400 hover:text-emerald-400 transition-colors">
                    Om os
                  </Link>
                </li>
                <li>
                  <Link to="/galleri" className="text-stone-400 hover:text-emerald-400 transition-colors">
                    Billedgalleri
                  </Link>
                </li>
                <li>
                  <Link to="/kontakt" className="text-stone-400 hover:text-emerald-400 transition-colors">
                    Kontakt os
                  </Link>
                </li>
                <li className="text-stone-500 pt-2 text-xs border-t border-stone-800">
                  <span className="block font-semibold text-stone-400 uppercase mb-1">CERTIFICERING</span>
                  <div className="space-y-3">
                    <div>Godkendt til at arbejde i PEFC-certificerede skove</div>
                    <div className="pt-1 flex items-center gap-3 flex-wrap">
                      <img 
                        src="https://assets.auxodev.dk/cdn-cgi/image/width=320,quality=90,format=auto/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/dme-logo-biomasseproducent-2023-rgb.png" 
                        alt="DME Godkendt Biomasseproducent" 
                        className="h-20 w-auto object-contain brightness-95 hover:brightness-100 transition-all"
                      />
                      <img 
                        src="https://assets.auxodev.dk/assets/c0eeaa23-7b26-43fb-9bea-da1f57f78d16/2954988060181262762503482.svg" 
                        alt="Certificering" 
                        className="h-20 w-auto object-contain brightness-95 hover:brightness-100 transition-all"
                      />
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            {/* Column 4: Contact */}
            <div className="space-y-6">
              <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-6">Kontakt</h3>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                  <span className="text-stone-400">
                    {COMPANY_INFO.name}<br />
                    {COMPANY_INFO.address}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-emerald-500 shrink-0" />
                  <a href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`} className="text-stone-400 hover:text-emerald-400 transition-colors whitespace-nowrap">
                    {COMPANY_INFO.phone}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-emerald-500 shrink-0" />
                  <a href={`mailto:${COMPANY_INFO.email}`} className="text-stone-400 hover:text-emerald-400 transition-colors whitespace-nowrap">
                    {COMPANY_INFO.email}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Copyright */}
          <div className="mt-16 pt-8 border-t border-stone-800 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-stone-500">
            <p>© {new Date().getFullYear()} {COMPANY_INFO.name}. Alle rettigheder forbeholdes.</p>
            <div className="flex items-center gap-3">
              <a href="https://www.iubenda.com/privacy-policy/16504718/cookie-policy" className="iubenda-nostyle iubenda-noiframe iubenda-embed text-stone-400 hover:text-emerald-400 transition-colors" title="Cookiepolitik ">Cookiepolitik</a>
              <span className="text-stone-700">•</span>
              <a href="https://www.iubenda.com/privacy-policy/16504718" className="iubenda-nostyle iubenda-noiframe iubenda-embed text-stone-400 hover:text-emerald-400 transition-colors" title="Privatlivspolitik ">Privatlivspolitik</a>
            </div>
            <p className="flex items-center gap-1">
              <span>Skoventreprenør & Naturpleje</span>
              <span className="text-stone-700">•</span>
              <span>Nordjylland</span>
            </p>
          </div>
        </div>
      </footer>
      {/* Floating CTA Buttons - Follows along on scroll */}
      {/* Desktop Sticky Floating Dock (Right Side) */}
      <aside 
        aria-label="Hurtig kontakt" 
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 hidden md:flex flex-col gap-2.5 items-end select-none pointer-events-auto"
      >
        {/* Ring op CTA */}
        <a 
          href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`}
          className="group flex items-center gap-3 bg-swamp-950/95 hover:bg-swamp-900 text-white pl-3.5 pr-4 py-2.5 rounded-l-2xl border-y border-l border-swamp-700/80 shadow-2xl backdrop-blur-md transition-all duration-300 hover:translate-x-[-4px] hover:border-swamp-500"
          title={`Ring til os på ${COMPANY_INFO.phone}`}
        >
          <div className="w-9 h-9 rounded-xl bg-swamp-850 border border-swamp-700 text-swamp-300 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
            <Phone className="w-4 h-4 text-swamp-300 stroke-[2.5]" />
          </div>
          <div className="text-left leading-tight">
            <span className="block text-[10px] font-bold uppercase tracking-widest text-swamp-300">Ring direkte</span>
            <span className="block text-xs sm:text-sm font-black text-white whitespace-nowrap">{COMPANY_INFO.phone}</span>
          </div>
        </a>

        {/* Få et tilbud CTA */}
        <Link 
          to="/kontakt"
          className="group flex items-center gap-3 bg-swamp-900 hover:bg-swamp-800 text-white pl-3.5 pr-4 py-2.5 rounded-l-2xl border-y border-l border-swamp-600 shadow-2xl shadow-black/50 backdrop-blur-md transition-all duration-300 hover:translate-x-[-4px] font-bold"
          title="Få et uforpligtende tilbud"
        >
          <div className="w-9 h-9 rounded-xl bg-swamp-950 border border-swamp-700 text-swamp-300 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
            <ArrowRight className="w-4 h-4 text-swamp-300 stroke-[3]" />
          </div>
          <div className="text-left leading-tight">
            <span className="block text-[10px] font-bold uppercase tracking-widest text-swamp-300 opacity-90">Uforpligtende</span>
            <span className="block text-xs sm:text-sm font-black uppercase tracking-tight text-white whitespace-nowrap">Få et tilbud</span>
          </div>
        </Link>
      </aside>

      {/* Mobile Floating Action Buttons (Bottom-Right) */}
      <aside 
        aria-label="Hurtig kontakt på mobil" 
        className="fixed bottom-5 right-4 z-40 md:hidden flex flex-col gap-2.5 items-end pointer-events-auto"
      >
        <a 
          href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`}
          className="flex items-center gap-2 bg-swamp-950 border-2 border-swamp-700 text-white px-3.5 py-2.5 rounded-full shadow-2xl text-xs font-bold tracking-wide active:scale-95 transition-transform"
          aria-label={`Ring til ${COMPANY_INFO.phone}`}
        >
          <div className="w-6 h-6 rounded-full bg-swamp-850 text-swamp-300 flex items-center justify-center">
            <Phone className="w-3.5 h-3.5 text-swamp-300" />
          </div>
          <span>Ring op</span>
        </a>
        <Link 
          to="/kontakt"
          className="flex items-center gap-2 bg-swamp-900 border border-swamp-600 text-white px-3.5 py-2.5 rounded-full shadow-2xl text-xs font-bold tracking-wide uppercase active:scale-95 transition-transform"
          aria-label="Få et uforpligtende tilbud"
        >
          <ArrowRight className="w-3.5 h-3.5 text-swamp-300 stroke-[3]" />
          <span>Få tilbud</span>
        </Link>
      </aside>
    </div>
  );
}
